import {
  DisplayMessage,
  GetFileExtension,
  Uuidv4,
} from "../../../../../services/helpers";
import {
  DeleteRequest,
  GetRequest,
  UpdateRequest,
} from "../../../../../services/requestHelpers";
import { GenerateSAS, UploadFileInStorage } from "../../../../../services/uploadHelpers";
import { HttpMethods } from "../../../../../utils/constants";
import { MEDIA_ENTITY_TYPE_DIAGNOSTIC_FLOWCHART } from "../utils/constants";
import {
  mapDiagnosticFlows,
  mapDiagnosticFlowDetail,
} from "../mappers/diagnosticFlowMapper";

const baseUrl = "api/mobopro/v1/diagnosticflows";

const showErrors = (result) =>
  (result?.errors || []).forEach((error) => DisplayMessage(error, "error"));

export const getDiagnosticFlows = async (resources = {}) => {
  try {
    const response = await GetRequest(baseUrl);
    if (!response.ok) {
      showErrors(await response.json());
      return undefined;
    }
    return mapDiagnosticFlows(await response.json());
  } catch (err) {
    console.error(err);
    DisplayMessage(resources["ErrorFetchingDiagnosticFlows"], "error");
    return undefined;
  }
};

export const getDiagnosticFlowById = async (id, resources = {}) => {
  try {
    const response = await GetRequest(`${baseUrl}/${id}`);
    if (!response.ok) {
      showErrors(await response.json());
      return undefined;
    }
    return mapDiagnosticFlowDetail(await response.json());
  } catch (err) {
    console.error(err);
    DisplayMessage(resources["ErrorFetchingDiagnosticFlow"], "error");
    return undefined;
  }
};

export const createDiagnosticFlow = async (request, resources = {}) => {
  try {
    const response = await UpdateRequest(
      baseUrl,
      JSON.stringify(request),
      HttpMethods.Post
    );
    const result = await response.json();
    if (!response.ok) {
      showErrors(result);
      return undefined;
    }
    return result;
  } catch (err) {
    console.error(err);
    DisplayMessage(resources["ErrorCreatingDiagnosticFlow"], "error");
    return undefined;
  }
};

export const updateDiagnosticFlow = async (id, request, resources = {}) => {
  try {
    const response = await UpdateRequest(
      `${baseUrl}/${id}`,
      JSON.stringify(request),
      HttpMethods.Put
    );
    if (!response.ok) {
      showErrors(await response.json());
      return false;
    }
    return true;
  } catch (err) {
    console.error(err);
    DisplayMessage(resources["ErrorUpdatingDiagnosticFlow"], "error");
    return false;
  }
};

const linkDiagnosticFlowImage = async (id, flowchartImage) => {
  const response = await UpdateRequest(
    `${baseUrl}/${id}/flowchartimage`,
    JSON.stringify({ flowchartImage }),
    HttpMethods.Put
  );
  if (!response.ok) {
    showErrors(await response.json());
    return false;
  }
  return true;
};

export const uploadDiagnosticFlowImage = async (id, file, resources = {}) => {
  try {
    const sasRequest = JSON.stringify({
      minutes: 10,
      entityType: MEDIA_ENTITY_TYPE_DIAGNOSTIC_FLOWCHART,
      entityValue: Number(id),
      fileNames: [`${Uuidv4()}.${GetFileExtension(file.name)}`],
    });
    const sas = await GenerateSAS(sasRequest);
    if (!sas || !sas[0]) return false;

    const uploaded = await UploadFileInStorage(
      file,
      sas[0],
      file.type || "image/png",
      resources["DiagnosticFlowFailedToUploadImage"]
    );
    if (!uploaded) return false;

    return linkDiagnosticFlowImage(id, sas[0].blobUri);
  } catch (err) {
    console.error(err);
    DisplayMessage(resources["ErrorUploadingFlowchartImage"], "error");
    return false;
  }
};

export const deleteDiagnosticFlow = async (id, resources = {}) => {
  try {
    const response = await DeleteRequest(`${baseUrl}/${id}`);
    if (!response.ok) {
      showErrors(await response.json());
    }
    return response.ok;
  } catch (err) {
    console.error(err);
    DisplayMessage(resources["ErrorDeletingDiagnosticFlow"], "error");
    return false;
  }
};

export const getDiagnosticFlowTranslations = async (id) => {
  try {
    const response = await GetRequest(`${baseUrl}/${id}/translations`);
    if (!response.ok) return [];
    return await response.json();
  } catch (err) {
    return [];
  }
};

export const saveDiagnosticFlowTranslation = async (id, language, blob, resources = {}) => {
  try {
    const response = await UpdateRequest(
      `${baseUrl}/${id}/translations/${language}`,
      JSON.stringify({ jsonFlowTranslation: JSON.stringify(blob) }),
      HttpMethods.Put
    );
    if (!response.ok) {
      showErrors(await response.json());
      return false;
    }
    return true;
  } catch (err) {
    console.error(err);
    DisplayMessage(resources["ErrorSavingDiagnosticFlowTranslation"], "error");
    return false;
  }
};

export const translateWithAI = async (requestBody) => {
  const response = await UpdateRequest(
    "api/mobopro/v1/translate",
    JSON.stringify(requestBody),
    HttpMethods.Post
  );
  if (!response.ok) {
    throw new Error("Translation API error");
  }
  return await response.json();
};

export const deleteDiagnosticFlowTranslation = async (id, language) => {
  try {
    const response = await DeleteRequest(`${baseUrl}/${id}/translations/${language}`);
    // 404 = nothing was stored for this language, which is the desired end state
    return response.ok || response.status === 404;
  } catch (err) {
    return false;
  }
};
